import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/common/ui/button";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";

import {
  applyBundle,
  type BundleChange,
  type BundleMutationResult,
  retractBundle,
} from "./entitlements-api";

export interface BundleEditorProps {
  readonly scope: ScopeRef | null;
  /** Fired after a bundle write lands, so the panels re-read what now applies. */
  readonly onMutated: () => void;
}

/** Order-insensitive, so a config object round-tripping with different key order is not a change. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((key) => key in right && sameValue(left[key], right[key]));
}

/**
 * Whether a reported change actually moved anything.
 *
 * `applyBundle` reports one entry per MEMBER of the bundle, unconditionally, so
 * the length of `changes` is the bundle's size and not the number of values that
 * moved. Re-applying an already-applied bundle returns a full list in which
 * every `before` equals its `after`, and counting those as changes would tell an
 * operator that five capabilities moved when none did.
 */
function moved(change: BundleChange): boolean {
  if (change.before === null || change.after === null) return change.before !== change.after;
  return !(
    change.before.type === change.after.type && sameValue(change.before.value, change.after.value)
  );
}

function summarize(result: BundleMutationResult, action: "apply" | "retract"): string {
  const changed = result.changes.filter(moved).length;
  const radius = `Blast radius: ${result.blastRadius}.`;

  if (action === "retract") {
    // A retract of a bundle that was never applied is a successful no-op. Saying
    // "retracted" would report a change that did not happen. Unlike an apply,
    // every entry here is a real removal, so the raw length is the honest count.
    if (result.changes.length === 0) {
      return `${result.bundleName} was not applied here, so nothing changed.`;
    }
    const removed = result.changes.length === 1 ? "capability" : "capabilities";
    return `Retracted every version of ${result.bundleName}: ${result.changes.length} ${removed} removed. ${radius}`;
  }

  const label = `Applied ${result.bundleName}@${result.version ?? "?"}`;
  if (changed === 0) {
    // Reachable and ordinary: re-applying a bundle whose values are already in
    // force. The service still reports every member, so this is the one case
    // where the list length would be actively misleading.
    return `${label}: no values changed; all ${result.changes.length} already in force. ${radius}`;
  }
  // Agrees with the TOTAL, which is the noun being counted: "1 of 2 capabilities".
  const capabilities = result.changes.length === 1 ? "capability" : "capabilities";
  return `${label}: ${changed} of ${result.changes.length} ${capabilities} changed. ${radius}`;
}

/**
 * Apply or retract a versioned bundle at the selected scope (ENR-1131 AC 7).
 *
 * A bundle is a template: applying writes its member grants as bundle-sourced
 * rows, which resolution ranks BELOW an override at the same scope, so applying
 * a tier cannot silently undo a deliberate exception someone made.
 *
 * The name and version are free text because no endpoint lists the bundles that
 * exist; the service is the authority on whether one does, and answers with a
 * typed 404. Deliberately NOT re-validating the name format here: the rule lives
 * in the admin-api's `validateApplyBundle`, and a second copy in the console is
 * how that rule drifts (the scope-id rule already drifted once that way). Empty
 * fields disable the buttons; everything else is the server's answer to give.
 */
export function BundleEditor({ scope, onMutated }: BundleEditorProps): React.ReactElement {
  const [bundleName, setBundleName] = useState("");
  const [version, setVersion] = useState("");
  const [result, setResult] = useState<{
    readonly scopeKey: string;
    readonly value: BundleMutationResult;
    readonly action: "apply" | "retract";
  } | null>(null);
  const [error, setError] = useState<{
    readonly scopeKey: string;
    readonly message: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const currentKey = scope === null ? null : scopeKey(scope);

  /**
   * The scope on screen RIGHT NOW, mirrored during render rather than in an
   * effect.
   *
   * An effect runs after paint, so updating this there leaves a window in which
   * a response that resolves between the commit and the effect still matches the
   * PREVIOUS scope, is accepted, and fires `onMutated` to refresh the panels
   * around an outcome describing somewhere else. `use-effective.ts` documents
   * exactly this: "anything that invalidates it in an effect leaves a window
   * (effects run after paint) in which a late response for the previous scope is
   * still accepted."
   *
   * The mirror is idempotent and touches nothing outside this component, which
   * is what makes assigning it during render safe.
   */
  const shownFor = useRef<string | null>(currentKey);
  shownFor.current = currentKey;

  // A returning scope must not resurrect an old outcome: the panels have been
  // re-read since, so it would read as something that just happened.
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [scope]);

  async function run(
    action: "apply" | "retract",
    issuedFor: ScopeRef,
    call: () => Promise<BundleMutationResult>
  ): Promise<void> {
    const issuedKey = scopeKey(issuedFor);
    setBusy(true);
    setError(null);
    try {
      const value = await call();
      if (shownFor.current !== issuedKey) return;
      setResult({ scopeKey: issuedKey, value, action });
      onMutated();
    } catch (e) {
      if (shownFor.current !== issuedKey) return;
      setResult(null);
      setError({
        scopeKey: issuedKey,
        message: e instanceof Error ? e.message : "Request failed",
      });
    } finally {
      setBusy(false);
    }
  }

  /* Rendered only against the scope they belong to. The guard above stops a late
     response being stored at all; this stops one that IS stored from ever being
     painted under a different scope, whatever the timing. */
  const shownResult = result !== null && result.scopeKey === currentKey ? result : null;
  const shownError = error !== null && error.scopeKey === currentKey ? error : null;

  const named = bundleName.trim() !== "";
  const canApply = scope !== null && named && version.trim() !== "" && !busy;
  const canRetract = scope !== null && named && !busy;

  return (
    <div className="space-y-4" data-testid="bundle-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="bundle-name">Bundle</Label>
          <Input
            id="bundle-name"
            value={bundleName}
            onChange={(event) => setBundleName(event.target.value)}
            placeholder="starter"
            disabled={busy}
            data-testid="bundle-name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bundle-version">Version</Label>
          <Input
            id="bundle-version"
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="1.0.0"
            disabled={busy}
            data-testid="bundle-version"
          />
          <p className="text-xs text-muted-foreground">
            Applying only; a retract removes every version.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          data-testid="apply-bundle"
          disabled={!canApply}
          onClick={() => {
            if (scope && canApply) {
              void run("apply", scope, () =>
                applyBundle({ scope, bundleName: bundleName.trim(), version: version.trim() })
              );
            }
          }}
        >
          Apply bundle
        </Button>
        <Button
          variant="outline"
          data-testid="retract-bundle"
          disabled={!canRetract}
          onClick={() => {
            if (scope && canRetract) {
              // No version: the service removes every version of the named
              // bundle, and sending one would imply this removes only that one.
              void run("retract", scope, () =>
                retractBundle({ scope, bundleName: bundleName.trim() })
              );
            }
          }}
        >
          Retract bundle
        </Button>
      </div>

      {shownError !== null && (
        <p className="text-sm text-destructive" data-testid="bundle-error">
          {shownError.message}
        </p>
      )}
      {shownResult !== null && (
        <p className="text-sm text-muted-foreground" data-testid="bundle-result">
          {summarize(shownResult.value, shownResult.action)}
        </p>
      )}
    </div>
  );
}
